import type { ReactNode } from 'react';
import type { FabricaMenu } from '@/auth/fabricaMenus';
import type { Portal } from '@/routes/roles';
import { navigation, type NavItem, type NavSection } from './navigation';

/**
 * The sidebar from a role's Fabrica menu tree. Fabrica decides which menus a role sees,
 * their names and their order; the built-in navigation (navigation.tsx) still lends each
 * page its icon component and subtitle, matched by path, and each section its id and label.
 * Every built-in section counts, not only the portal's own: a role's menus may reach pages
 * of another portal (e.g. a role in the faculty portal may also have an /administrator page).
 */

interface PageLook {
  icon?: ReactNode;
  description?: string;
}

const walk = (items: NavItem[], out: Map<string, PageLook>) => {
  for (const item of items) {
    if (!out.has(item.to)) out.set(item.to, { ...(item.icon ? { icon: item.icon } : {}), ...(item.description ? { description: item.description } : {}) });
    walk(item.children ?? [], out);
  }
};

const pathsOf = (items: NavItem[]): string[] => items.flatMap((i) => [i.to, ...pathsOf(i.children ?? [])]);

const firstPath = (menu: FabricaMenu): string | null => menu.path ?? menu.children.map(firstPath).find((p) => p) ?? null;

function toItem(menu: FabricaMenu, looks: Map<string, PageLook>): NavItem | null {
  const to = firstPath(menu);
  if (!to) return null;
  const children = menu.children.map((c) => toItem(c, looks)).filter((c): c is NavItem => c !== null);
  const look = looks.get(menu.path ?? to);
  return {
    label: menu.label,
    to,
    ...(look?.icon ? { icon: look.icon } : {}),
    ...(look?.description ? { description: look.description } : {}),
    ...(children.length ? { children } : {}),
  };
}

export function navigationFromFabrica(portal: Portal, menus: FabricaMenu[]): NavSection[] {
  const builtIn = navigation;
  const looks = new Map<string, PageLook>();
  builtIn.forEach((s) => walk(s.items, looks));

  return menus
    .map((root): NavSection | null => {
      // A top-level menu with its own page is a section of one item.
      const items = (root.path ? [toItem({ ...root, children: [] }, looks)] : root.children.map((c) => toItem(c, looks)))
        .filter((i): i is NavItem => i !== null);
      if (items.length === 0) return null;
      // The built-in section sharing the most pages lends its id and label (or none, like the portals').
      const paths = new Set(pathsOf(items));
      const match = builtIn
        .map((s) => ({ s, shared: pathsOf(s.items).filter((p) => paths.has(p)).length }))
        .sort((a, b) => b.shared - a.shared)[0];
      const section = match && match.shared > 0 ? match.s : undefined;
      return {
        id: section?.id ?? `fabrica-${root.menuId}`,
        portals: [portal],
        ...(section ? (section.label ? { label: section.label } : {}) : { label: root.label }),
        items,
      };
    })
    .filter((s): s is NavSection => s !== null);
}

/**
 * True when a page is one the role does not have in Fabrica: it sits under a built-in menu of
 * any portal, but under none of the role's Fabrica menus. Every portal's menus count, so a
 * role reaching two portals cannot open the rest of the second one by URL.
 */
export function isMenuPageWithheld(fabricaPaths: string[], pathname: string): boolean {
  const under = (paths: string[]) => paths.some((p) => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`));
  const builtIn = navigation.flatMap((s) => pathsOf(s.items));
  return under(builtIn) && !under(fabricaPaths);
}
